'use client';

import { c as _c } from "react/compiler-runtime";
import './volunteer-event-info.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { GoogleMapEmbed } from '@sola/ark-core/atoms/google-map-embed/google-map-embed.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Dialog, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@sola/ark-core/molecules/dropdown-menu/dropdown-menu.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { ShareButtons, ShareButtonsTrigger } from '@sola/ark-skippy/molecules/share-buttons/share-buttons.tsx';
import { getPublicConfig } from '@sola/config/lib/getters.ts';
import type { GraphQLVolunteerSlot } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import type { VolunteerSlotContact } from '@sola/shared-utils/supporters/volunteers.ts';
import { VolunteerActionDialog, type VolunteerActionDialogProps } from '../volunteer-action-dialog/volunteer-action-dialog.tsx';
import { getVolunteerEventInfoData } from './volunteer-event-info.logic.ts';
type VolunteerEventMenuItemBaseProps = {
  iconName: IconNames;
  label: string;
  purpose?: 'neutral' | 'negative';
};
type VolunteerEventMenuItemProps = VolunteerEventMenuItemBaseProps & ({
  href: string;
  onClick?: never;
} | {
  href?: never;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
});
const VolunteerEventMenuItemContent: React.FC<Pick<VolunteerEventMenuItemBaseProps, 'iconName' | 'label'>> = t0 => {
  const $ = _c(5);
  const {
    iconName,
    label
  } = t0;
  let t1;
  if ($[0] !== iconName) {
    t1 = <ArkIcon className="VolunteerEventInfo__menu-item-icon" iconName={iconName} size="16" />;
    $[0] = iconName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <span className="VolunteerEventInfo__menu-item-content w-full px-comfy-sm py-snug-xl label-md label-potent"><span className="flex w-full items-center gap-snug-xl py-snug-sm">{t1}{label}</span></span>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const VolunteerEventMenuItem: React.FC<VolunteerEventMenuItemProps> = t0 => {
  const $ = _c(18);
  let href;
  let iconName;
  let label;
  let onClick;
  let t1;
  let triggerProps;
  if ($[0] !== t0) {
    ({
      href,
      onClick,
      iconName,
      label,
      purpose: t1,
      ...triggerProps
    } = t0);
    $[0] = t0;
    $[1] = href;
    $[2] = iconName;
    $[3] = label;
    $[4] = onClick;
    $[5] = t1;
    $[6] = triggerProps;
  } else {
    href = $[1];
    iconName = $[2];
    label = $[3];
    onClick = $[4];
    t1 = $[5];
    triggerProps = $[6];
  }
  const purpose = t1 === undefined ? "neutral" : t1;
  const t2 = purpose === "negative";
  let t3;
  if ($[7] !== t2) {
    t3 = cn("VolunteerEventInfo__menu-item flex w-full no-underline", {
      "VolunteerEventInfo__menu-item--negative": t2
    });
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const className = t3;
  let t4;
  if ($[9] !== className || $[10] !== href || $[11] !== iconName || $[12] !== label || $[13] !== onClick) {
    t4 = href ? <Link className={className} href={href}><VolunteerEventMenuItemContent iconName={iconName} label={label} /></Link> : <button className={className} onClick={onClick} type="button"><VolunteerEventMenuItemContent iconName={iconName} label={label} /></button>;
    $[9] = className;
    $[10] = href;
    $[11] = iconName;
    $[12] = label;
    $[13] = onClick;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t4 || $[16] !== triggerProps) {
    t5 = <DropdownMenuItem asChild={true} {...triggerProps}>{t4}</DropdownMenuItem>;
    $[15] = t4;
    $[16] = triggerProps;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
interface VolunteerEventMenuProps {
  canCancelShifts: boolean;
  eventDetailUrl: string;
  onCancelAllShifts: () => void;
  title: string;
}
const VolunteerEventMenu: React.FC<VolunteerEventMenuProps> = t0 => {
  const $ = _c(30);
  const {
    canCancelShifts,
    eventDetailUrl,
    onCancelAllShifts,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("volunteers:eventMenu.moreOptions");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArkIcon iconName="dots-horizontal" size="24" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <DropdownMenuTrigger><ButtonStyle className="VolunteerEventInfo__menu-trigger" purpose="neutral" variant="tertiary" iconOnly={true}><button title={t2} type="button">{t3}</button></ButtonStyle></DropdownMenuTrigger>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("volunteers:eventMenu.viewDetails");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== eventDetailUrl || $[9] !== t5) {
    t6 = <VolunteerEventMenuItem href={eventDetailUrl} iconName="eye" label={t5} />;
    $[8] = eventDetailUrl;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("volunteers:eventMenu.shareDetails");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <ShareButtonsTrigger><VolunteerEventMenuItem iconName="share-02" label={t7} /></ShareButtonsTrigger>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== canCancelShifts || $[16] !== onCancelAllShifts || $[17] !== t) {
    t9 = canCancelShifts && <><Divider purpose="neutral" /><VolunteerEventMenuItem iconName="x-circle" label={t("volunteers:eventMenu.cancelAllShifts")} onClick={onCancelAllShifts} purpose="negative" /></>;
    $[15] = canCancelShifts;
    $[16] = onCancelAllShifts;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t8 || $[21] !== t9) {
    t10 = <DropdownMenuContent align="end" className="VolunteerEventInfo__menu overflow-hidden rounded-md" sideOffset={6}>{t6}{t8}{t9}</DropdownMenuContent>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t4) {
    t11 = <DropdownMenu>{t4}{t10}</DropdownMenu>;
    $[23] = t10;
    $[24] = t4;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== eventDetailUrl || $[27] !== t11 || $[28] !== title) {
    t12 = <ShareButtons shareTitle={title} shareUrl={eventDetailUrl}>{t11}</ShareButtons>;
    $[26] = eventDetailUrl;
    $[27] = t11;
    $[28] = title;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
interface VolunteerEventLocationProps {
  address?: string;
  locationName?: string;
}
const VolunteerEventLocation: React.FC<VolunteerEventLocationProps> = t0 => {
  const $ = _c(14);
  const {
    address,
    locationName
  } = t0;
  if (!locationName) {
    return null;
  }
  if (!address) {
    let t1;
    if ($[0] !== locationName) {
      t1 = <div>{locationName}</div>;
      $[0] = locationName;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getPublicConfig();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    NEXT_PUBLIC_GOOGLE_MAPS_KEY
  } = t1;
  let t2;
  if ($[3] !== locationName) {
    t2 = <DialogTrigger><LinkStyle size="lg" weightOverride="light" inline={true} noUnderline={true}><button type="button">{locationName}</button></LinkStyle></DialogTrigger>;
    $[3] = locationName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== address) {
    t3 = <GoogleMapEmbed apiKey={NEXT_PUBLIC_GOOGLE_MAPS_KEY} query={address} />;
    $[5] = address;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== address || $[8] !== locationName || $[9] !== t3) {
    t4 = <DialogContent title={locationName} supportingText={address} size="lg" icon="marker-pin-01">{t3}</DialogContent>;
    $[7] = address;
    $[8] = locationName;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t4) {
    t5 = <Dialog>{t2}{t4}</Dialog>;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export interface VolunteerEventInfoProps {
  address?: string;
  className?: string;
  contacts: VolunteerSlotContact[];
  endDate: Date;
  eventDetailUrl: string;
  locationName?: string;
  onCancel: VolunteerActionDialogProps['onSubmit'];
  shiftCount: number;
  slots: GraphQLVolunteerSlot[];
  startDate: Date;
  title: string;
}
export const VolunteerEventInfo: React.FC<VolunteerEventInfoProps> = t0 => {
  const $ = _c(61);
  const {
    address,
    className,
    contacts,
    endDate,
    eventDetailUrl,
    locationName,
    onCancel,
    shiftCount,
    slots,
    startDate,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    i18n,
    t
  } = useTranslation(t1);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  let t2;
  if ($[1] !== endDate) {
    t2 = () => endDate < new Date();
    $[1] = endDate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [hasEventEnded] = useState(t2);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== className || $[4] !== endDate || $[5] !== i18n.language || $[6] !== i18n.resolvedLanguage || $[7] !== startDate || $[8] !== t) {
    const {
      dateSections,
      dateTime,
      isSingleDay
    } = getVolunteerEventInfoData({
      endDate,
      language: i18n.resolvedLanguage ?? i18n.language,
      startDate
    });
    const t8 = isSingleDay ? "volunteers:dateLabel" : "volunteers:dateRangeLabel";
    let t9;
    if ($[14] !== endDate || $[15] !== startDate || $[16] !== t || $[17] !== t8) {
      t9 = t(t8, {
        endDate,
        startDate
      });
      $[14] = endDate;
      $[15] = startDate;
      $[16] = t;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    const dateLabel = t9;
    if ($[19] !== className) {
      t7 = cn("VolunteerEventInfo flex items-start gap-comfy-md", className);
      $[19] = className;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t3 = dateLabel;
    t4 = "VolunteerEventInfo__date-card flex shrink-0 flex-col rounded-md overflow-hidden text-center";
    t5 = dateTime;
    t6 = dateSections.map(_temp);
    $[3] = className;
    $[4] = endDate;
    $[5] = i18n.language;
    $[6] = i18n.resolvedLanguage;
    $[7] = startDate;
    $[8] = t;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t8 = <time aria-label={t3} className={t4} dateTime={t5}>{t6}</time>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== title) {
    t9 = <Text variant={Variants.Heading} size={Sizes.Small} prominence={Prominence.Amplified}>{title}</Text>;
    $[26] = title;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== shiftCount || $[29] !== t) {
    t10 = t("volunteers:shiftCount", {
      count: shiftCount
    });
    $[28] = shiftCount;
    $[29] = t;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10) {
    t11 = <Badge color="brand" label={t10} />;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t9) {
    t12 = <div className="flex flex-wrap items-center gap-snug-xl mb-snug-md">{t9}{t11}</div>;
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== address || $[37] !== locationName) {
    t13 = <VolunteerEventLocation address={address} locationName={locationName} />;
    $[36] = address;
    $[37] = locationName;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== address) {
    t14 = address && <div className="label-neutral-mild">{address}</div>;
    $[39] = address;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t12 || $[42] !== t13 || $[43] !== t14) {
    t15 = <div className="flex min-w-0 flex-col gap-snug-md">{t12}{t13}{t14}</div>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== eventDetailUrl || $[46] !== hasEventEnded || $[47] !== slots || $[48] !== title) {
    t16 = !hasEventEnded && <div className="ml-auto"><VolunteerEventMenu canCancelShifts={slots.length > 0} eventDetailUrl={eventDetailUrl} onCancelAllShifts={() => setIsCancelDialogOpen(true)} title={title} /></div>;
    $[45] = eventDetailUrl;
    $[46] = hasEventEnded;
    $[47] = slots;
    $[48] = title;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== contacts || $[51] !== isCancelDialogOpen || $[52] !== onCancel || $[53] !== slots) {
    t17 = <VolunteerActionDialog action="cancel" contacts={contacts} onOpenChange={setIsCancelDialogOpen} onSubmit={onCancel} open={isCancelDialogOpen} slots={slots} />;
    $[50] = contacts;
    $[51] = isCancelDialogOpen;
    $[52] = onCancel;
    $[53] = slots;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== t15 || $[56] !== t16 || $[57] !== t17 || $[58] !== t7 || $[59] !== t8) {
    t18 = <article className={t7}>{t8}{t15}{t16}{t17}</article>;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t7;
    $[59] = t8;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  return t18;
};
export interface VolunteerEventInfoSkeletonProps {
  className?: string;
}
export const VolunteerEventInfoSkeleton: React.FC<VolunteerEventInfoSkeletonProps> = t0 => {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("VolunteerEventInfo flex items-start gap-comfy-md", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton width={64} height={55} borderRadius=".5rem" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex min-w-0 flex-col gap-snug-lg"><div className="flex flex-wrap items-center gap-snug-xl mb-snug-md"><Skeleton height={26} width={250} /><Skeleton height={27.5} width={64} borderRadius="var(--outline-radius-lg)" /></div><Skeleton height={18} width={200} /><Skeleton height={18} width={300} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <article aria-hidden="true" className={t1}>{t2}{t3}</article>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
function _temp(t0) {
  const {
    day,
    month
  } = t0;
  return <span className="flex flex-col" key={`${month}-${day}`}><Text variant={Variants.Label} size={Sizes.Small} prominence={Prominence.Potent} asChild={true}><span className="VolunteerEventInfo__date-month px-snug-xl py-snug-md">{month}</span></Text><Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent} asChild={true}><span className="px-snug-xl pt-snug-md pb-snug-lg">{day}</span></Text></span>;
}